import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';

import { Attachments } from './Attachments';
import { buildInitialAttachmentSlotStates } from './attachmentSlots';
import { AutomatedDecisionNotice } from './AutomatedDecisionNotice';
import { CandidateDetails } from './CandidateDetails';
import {
  PRIVACY_CHECKBOX_ID,
  WCAG_CANDIDATE_ERROR_CLASS,
  WCAG_FORM_ID,
  WCAG_INLINE_ERROR_CLASS
} from './constants';
import { FormActions } from './FormActions';
import { Preview } from './Preview';
import { PrivacyAcknowledgement } from './PrivacyAcknowledgement';
import { Questions } from './Questions';
import { parseQuestionDate, validateQuestions } from './questionValidation';
import { SourceSelection } from './SourceSelection';
import { classes } from './styles';
import AIInterviewModal from '../AIStudio/AIInterviewModal/AIInterviewModal';

import type { Answer } from './questionTypes';
import type {
  AttachmentConfig,
  AttachmentSlotState,
  AttachmentText,
  AutoFillConfig,
  AutoFillResumeFile,
  AutomatedDecisionNoticeConfig,
  CandidateDetailsText,
  PreviewSnapshot,
  PrivacyConfig,
  QuestionSnapshot,
  SourceOption
} from './types';

interface ApplicationFlowProps {
  candidateFormHtml: string;
  personalInformationLabel: string;
  previewAriaLabel: string;
  previewInfoBannerText: string;
  nothingEnteredText?: string;
  nothingSubmittedText?: string;
  candidateDetailsText: CandidateDetailsText;
  attachmentText: AttachmentText;
  answers?: string;
  jobId: number;
  aiInterviewEnabled: boolean;
  sourceValue: string;
  saveForLaterText: string;
  confirmSaveMessage: string;
  confirmSaveNote: string;
  saveDialogTitle: string;
  saveDialogConfirmText: string;
  cancelText: string;
  startOverDialogTitle: string;
  startOverDialogMessage: string;
  nextText: string;
  showSaveForLater: boolean;
  attachments?: AttachmentConfig[];
  sources?: SourceOption[];
  autoFillConfig?: AutoFillConfig;
  editButtonText: string;
  startOverButtonText: string;
  confirmButtonText: string;
  privacy?: PrivacyConfig;
  automatedDecisionNotice?: AutomatedDecisionNoticeConfig;
}

// Helper types for the submit pipeline.

type ValidatableControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

interface StageResult {
  valid: boolean;
  surface: () => void;
}

interface CandidateFieldError {
  control: ValidatableControl;
  message?: string;
}

interface ValidationDeps {
  form: HTMLFormElement;
  attachments: AttachmentConfig[];
  attachmentSlotStatesRef: React.MutableRefObject<Record<string, AttachmentSlotState>>;
  sourcesLength: number;
  privacy?: PrivacyConfig;
  runQuestionValidation: () => { isValid: boolean; surfaceErrors: () => void };
  setAttachmentErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setSourceValidationError: React.Dispatch<React.SetStateAction<string>>;
  setPrivacyError: React.Dispatch<React.SetStateAction<string>>;
}

// DOM helpers used by the submit pipeline.
const EDIT_CONTAINER_SELECTOR = '[data-react-class="WCAG/EditContainer"]';
const SKIP_TO_FORM_LINK_ID = 'wcag-skip-to-form';
const SKIP_TO_ERRORS_LINK_ID = 'wcag-skip-to-errors';
const NEXT_BUTTON_ID = 'application_submit';
const START_OVER_COMMIT_VALUE = 'Start over';
const NATIVE_FOCUSABLE_SELECTOR =
  'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])';
// Shared empty defaults: an inline `= []` is a new array on every render, which re-runs the submit
// effect after Next and its cleanup wipes the validation errors it just surfaced.
const NO_ATTACHMENTS: AttachmentConfig[] = [];
const NO_SOURCES: SourceOption[] = [];

const focusWithoutScroll = (target: HTMLElement): void => {
  target.focus({ preventScroll: true });
};

const preferredScrollBehavior = (): ScrollBehavior =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

const focusWithTabindexCleanup = (el: HTMLElement): void => {
  if (!el.matches(NATIVE_FOCUSABLE_SELECTOR)) {
    const hadTabindex = el.hasAttribute('tabindex');
    if (!hadTabindex) {
      el.setAttribute('tabindex', '-1');
      el.addEventListener('blur', () => el.removeAttribute('tabindex'), { once: true });
    }
  }
  focusWithoutScroll(el);
};

const setSkipToErrorsVisible = (visible: boolean): void => {
  const link = document.getElementById(SKIP_TO_ERRORS_LINK_ID);
  if (link) {
    link.style.display = visible ? '' : 'none';
  }
};

const findAllInvalidControls = (container: ParentNode | null): ValidatableControl[] => {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll<ValidatableControl>('input, textarea, select')
  ).filter((control) => control.willValidate && !control.validity.valid);
};

const findSaveCandidateErrors = (container: ParentNode | null): CandidateFieldError[] => {
  if (!container) return [];

  return Array.from(container.querySelectorAll<ValidatableControl>('[data-save-required="true"]'))
    .filter((control) => control.willValidate && !control.validity.valid)
    .map((control) => {
      const saveErrorMessage = control.dataset.saveErrorMessage?.trim();

      return {
        control,
        message: control.validity.valueMissing && saveErrorMessage ? saveErrorMessage : undefined
      };
    });
};

const showCandidateFieldError = (ctrl: ValidatableControl, message?: string): void => {
  const key = ctrl.id || ctrl.name;
  const errorId = `candidate-error-${key}`;
  const errorEl = document.createElement('div');
  errorEl.id = errorId;
  errorEl.className = WCAG_CANDIDATE_ERROR_CLASS;
  errorEl.setAttribute('role', 'alert');
  errorEl.setAttribute('data-validation-error', '');
  errorEl.setAttribute('aria-live', 'polite');
  errorEl.style.cssText = classes.candidateDetails.phoneErrorCssText;
  errorEl.textContent = message ?? ctrl.validationMessage;
  ctrl.parentNode?.insertBefore(errorEl, ctrl.nextSibling);
  ctrl.setAttribute('aria-invalid', 'true');
  ctrl.setAttribute('aria-describedby', errorId);
  ctrl.style.setProperty(
    'border-color',
    classes.candidateDetails.phoneErrorBorderColor,
    'important'
  );
};

const clearCandidateErrors = (): void => {
  const section = document.getElementById('candidate-details');
  if (!section) return;
  // Remove both pipeline errors and inline phone/email errors.
  section
    .querySelectorAll(`.${WCAG_CANDIDATE_ERROR_CLASS}, .${WCAG_INLINE_ERROR_CLASS}`)
    .forEach((el) => el.remove());
  section.querySelectorAll<ValidatableControl>('[aria-invalid="true"]').forEach((ctrl) => {
    ctrl.removeAttribute('aria-invalid');
    ctrl.removeAttribute('aria-describedby');
    ctrl.style.removeProperty('border-color');
  });
};

const handleCandidateInput = (e: Event): void => {
  const target = e.target as ValidatableControl;
  const key = target.id || target.name;
  if (!key) return;
  const errorId = `candidate-error-${key}`;
  const errorEl = document.getElementById(errorId);
  if (!errorEl) return;

  errorEl.remove();

  // Only clear pipeline-owned ARIA state. Inline validators may already
  // have claimed aria-describedby with their own error ID.
  if (target.getAttribute('aria-describedby') !== errorId) {
    return;
  }

  target.removeAttribute('aria-invalid');
  target.removeAttribute('aria-describedby');
  target.style.removeProperty('border-color');
};

const findFirstValidationErrorTarget = (): HTMLElement | null => {
  const editContainer = document.querySelector<HTMLElement>(EDIT_CONTAINER_SELECTOR);
  if (!editContainer) return null;

  const invalidControl = editContainer.querySelector<ValidatableControl>(
    'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"]'
  );
  if (invalidControl instanceof HTMLElement) {
    if (
      invalidControl instanceof HTMLInputElement &&
      invalidControl.type.toLowerCase() === 'file'
    ) {
      const attachmentGroup = invalidControl.closest<HTMLElement>('[id^="attachment-group-"]');
      if (attachmentGroup) return attachmentGroup;
    }
    return invalidControl;
  }

  const firstAlert = editContainer.querySelector<HTMLElement>(
    '[data-validation-error][role="alert"]'
  );
  if (!firstAlert) return null;

  const siblingControl = firstAlert.previousElementSibling;
  if (
    siblingControl instanceof HTMLInputElement ||
    siblingControl instanceof HTMLTextAreaElement ||
    siblingControl instanceof HTMLSelectElement
  ) {
    return siblingControl;
  }

  const questionContainer = firstAlert.closest<HTMLElement>('[id^="question-"]');
  if (questionContainer) return questionContainer;

  const attachmentGroup = firstAlert.closest<HTMLElement>('[id^="attachment-group-"]');
  if (attachmentGroup) return attachmentGroup;

  if (firstAlert.closest('[aria-labelledby="source-selection-heading"]')) {
    const sourceInput = document.getElementById('source-input');
    if (sourceInput instanceof HTMLElement) {
      return sourceInput;
    }
  }

  const interactiveChild = firstAlert.querySelector<HTMLElement>(NATIVE_FOCUSABLE_SELECTOR);
  if (interactiveChild) {
    return interactiveChild;
  }

  return firstAlert;
};

const resolveCommitValue = (
  submitter: HTMLButtonElement | HTMLInputElement | null
): string | null => {
  // Only accept explicit button commits: Next, Save, Confirm.
  const val = submitter?.value?.trim();
  return val?.length ? val : null;
};

const lockSubmitterForCommit = (
  form: HTMLFormElement,
  submitter: HTMLButtonElement | HTMLInputElement,
  commitValue: string
): void => {
  const commitInput = document.createElement('input');
  commitInput.type = 'hidden';
  commitInput.name = 'commit';
  commitInput.value = commitValue;
  commitInput.setAttribute('data-submit-lock', 'true');
  form.appendChild(commitInput);
  submitter.setAttribute('disabled', 'disabled');
};

// Build ordered validation stages. runPipeline stops at first invalid stage.

const buildValidationStages = (deps: ValidationDeps): (() => StageResult)[] => {
  const {
    form,
    attachments,
    attachmentSlotStatesRef,
    sourcesLength,
    privacy,
    runQuestionValidation,
    setAttachmentErrors,
    setSourceValidationError,
    setPrivacyError
  } = deps;

  const candidateStage = (): StageResult => {
    const section = document.getElementById('candidate-details');
    const invalidControls = findAllInvalidControls(section);
    const firstInvalid = invalidControls[0] ?? null;
    return {
      valid: invalidControls.length === 0,
      surface: () => {
        invalidControls.forEach((control) => showCandidateFieldError(control));
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'center' });
          focusWithoutScroll(firstInvalid);
        }
      }
    };
  };

  const questionStage = (): StageResult => {
    const result = runQuestionValidation();
    return {
      valid: result.isValid,
      surface: result.surfaceErrors
    };
  };

  const attachmentStage = (): StageResult => {
    const requiredMissing = attachments.filter((attachment) => {
      if (!attachment.mandatory) return false;
      const slot = attachmentSlotStatesRef.current[attachment.key];
      return !(slot?.status === 'existing' || slot?.status === 'selected');
    });
    const firstMissing = requiredMissing[0];
    const errorsByKey = requiredMissing.reduce<Record<string, string>>((acc, attachment) => {
      acc[attachment.key] = 'Please upload this required attachment.';
      return acc;
    }, {});

    return {
      valid: requiredMissing.length === 0,
      surface: () => {
        if (!firstMissing) return;
        setAttachmentErrors(errorsByKey);

        const group = document.getElementById(`attachment-group-${firstMissing.key}`);
        if (group instanceof HTMLElement) {
          group.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'center' });
          focusWithoutScroll(group);
        }
      }
    };
  };

  const sourceStage = (): StageResult => {
    if (sourcesLength === 0) {
      return { valid: true, surface: () => {} };
    }
    const formData = new FormData(form);
    const sourceId = String(formData.get('application[source_id]') ?? '').trim();
    const missing = sourceId.length === 0;
    return {
      valid: !missing,
      surface: () => {
        setSourceValidationError('Please select a source before continuing.');
        const sourceInput = document.getElementById('source-input');
        const sourceHeading = document.getElementById('source-selection-heading');
        const target = sourceInput instanceof HTMLElement ? sourceInput : sourceHeading;
        target?.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'center' });
        if (sourceInput instanceof HTMLElement) {
          focusWithoutScroll(sourceInput);
        }
      }
    };
  };

  const privacyStage = (): StageResult => {
    if (!privacy) return { valid: true, surface: () => {} };

    const checkbox = document.getElementById(PRIVACY_CHECKBOX_ID);
    if (!(checkbox instanceof HTMLInputElement)) {
      return { valid: true, surface: () => {} };
    }

    const valid = checkbox.checked;
    return {
      valid,
      surface: () => {
        setPrivacyError(privacy.errorText);
        checkbox.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'center' });
        focusWithoutScroll(checkbox);
      }
    };
  };

  // Privacy first: matches DOM order (topmost section in EditContainer) and gates
  // submission as a consent step before any other field validation surfaces.
  return [privacyStage, candidateStage, questionStage, attachmentStage, sourceStage];
};

const buildSaveCandidateStage = (): StageResult => {
  const section = document.getElementById('candidate-details');
  const candidateErrors = findSaveCandidateErrors(section);
  const firstInvalid = candidateErrors[0]?.control ?? null;

  return {
    valid: candidateErrors.length === 0,
    surface: () => {
      candidateErrors.forEach(({ control, message }) => showCandidateFieldError(control, message));
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'center' });
        focusWithoutScroll(firstInvalid);
      }
    }
  };
};

// Run stages in order and return the first blocker. Stage order corresponds
// to top-down DOM order so the surfaced error always sits at or above any
// downstream still-invalid stage. The producer of the stage list
// (`buildValidationStages`) is the source of truth for that ordering — see
// the comment above its `return [...]` line.
const runPipeline = (
  stages: (() => StageResult)[]
): { allValid: boolean; surfaceFirstBlocker: () => void } => {
  let allValid = true;
  let surfaceFirstBlocker = () => {};
  stages.some((stage) => {
    const result = stage();
    if (!result.valid) {
      allValid = false;
      surfaceFirstBlocker = result.surface;
      return true;
    }
    return false;
  });
  return { allValid, surfaceFirstBlocker };
};

// Static candidate fields shown in preview. Missing DOM fields are skipped.

const DEFAULT_NOTHING_ENTERED_TEXT = 'Nothing entered';
const DEFAULT_NOTHING_SUBMITTED_TEXT = 'Nothing submitted';

const CANDIDATE_FIELD_MAP: readonly { key: string; fullWidth?: boolean }[] = [
  { key: 'application[candidate_attributes][title]', fullWidth: true },
  { key: 'application[candidate_attributes][firstname]' },
  { key: 'application[candidate_attributes][lastname]' },
  { key: 'application[candidate_attributes][email]' },
  { key: 'application[candidate_attributes][phone1]' },
  { key: 'application[candidate_attributes][phone2]' },
  { key: 'application[candidate_attributes][address_attributes][street_number]' },
  { key: 'application[candidate_attributes][address_attributes][street1]' },
  { key: 'application[candidate_attributes][address_attributes][street_type]' },
  { key: 'application[candidate_attributes][address_attributes][street2]' },
  { key: 'application[candidate_attributes][address_attributes][suburb]' },
  { key: 'application[candidate_attributes][address_attributes][postcode]' },
  { key: 'application[candidate_attributes][address_attributes][country_id]' },
  { key: 'application[candidate_attributes][address_attributes][state_id]' },
  { key: 'application[candidate_attributes][gender]' },
  { key: 'application[candidate_attributes][employee_id]' },
  { key: 'application[candidate_attributes][linkedin_url]' }
];

const ApplicationFlow: React.FC<ApplicationFlowProps> = ({
  candidateFormHtml,
  personalInformationLabel,
  previewAriaLabel,
  previewInfoBannerText,
  nothingEnteredText = DEFAULT_NOTHING_ENTERED_TEXT,
  nothingSubmittedText = DEFAULT_NOTHING_SUBMITTED_TEXT,
  candidateDetailsText,
  attachmentText,
  answers,
  jobId,
  aiInterviewEnabled,
  sourceValue,
  saveForLaterText,
  confirmSaveMessage,
  confirmSaveNote,
  saveDialogTitle,
  saveDialogConfirmText,
  cancelText,
  startOverDialogTitle,
  startOverDialogMessage,
  nextText,
  showSaveForLater,
  attachments = NO_ATTACHMENTS,
  sources = NO_SOURCES,
  autoFillConfig,
  editButtonText,
  startOverButtonText,
  confirmButtonText,
  privacy,
  automatedDecisionNotice
}) => {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [snapshot, setSnapshot] = useState<PreviewSnapshot>({
    candidateFields: [],
    questions: [],
    attachments: [],
    hasAttachmentsSection: attachments.length > 0,
    hasQuestionsSection: false,
    hasSourceSection: sources.length > 0,
    source: null,
    privacy: null
  });
  const [sourceValidationError, setSourceValidationError] = useState<string>('');
  const [attachmentErrors, setAttachmentErrors] = useState<Record<string, string>>({});
  const [privacyError, setPrivacyError] = useState<string>('');
  const [selectedResumeFile, setSelectedResumeFile] = useState<AutoFillResumeFile>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [startOverDialogOpen, setStartOverDialogOpen] = useState(false);
  const saveConfirmedRef = useRef(false);
  const submittingRef = useRef(false);
  const startOverSubmitRef = useRef<HTMLInputElement | null>(null);
  const slotStatesRef = useRef<Record<string, AttachmentSlotState>>(
    buildInitialAttachmentSlotStates(attachments)
  );
  const resumeAttachment = useMemo(
    () => attachments.find((attachment) => attachment.isResume),
    [attachments]
  );

  // Parse server answers once from props.
  const parsedQuestionAnswers = useMemo<Answer[]>(() => {
    if (!answers) return [];
    try {
      const parsed = typeof answers === 'string' ? (JSON.parse(answers) as Answer[]) : answers;
      // Normalize legacy server dates (YYYY-MM-DD) to the frontend format (DD/MM/YYYY)
      return parsed.map((answer) => {
        if (answer.field?.field_type === 'date_field' && answer.data) {
          const date = parseQuestionDate(String(answer.data), { format: 'YYYY-MM-DD' });
          if (date.isValid()) return { ...answer, data: date.format('DD/MM/YYYY') };
        }
        return answer;
      });
    } catch {
      return [];
    }
  }, [answers]);

  // Mirror latest answers for submit-time validation/snapshot.
  const questionAnswersRef = useRef<Answer[]>(parsedQuestionAnswers);
  const [questionErrors, setQuestionErrors] = useState<Record<number, string>>({});
  const [questionFocusSignal, setQuestionFocusSignal] = useState<{
    fieldId: number;
    tick: number;
  } | null>(null);
  const focusTickRef = useRef(0);

  const handleAnswersChange = useCallback(
    (updatedAnswers: Answer[], changedFieldId: number | null) => {
      questionAnswersRef.current = updatedAnswers;

      if (changedFieldId != null) {
        setQuestionErrors((prev) => {
          const nextEntries = Object.entries(prev).filter(
            ([fieldId]) => Number(fieldId) !== changedFieldId
          );
          return nextEntries.length === Object.keys(prev).length
            ? prev
            : Object.fromEntries(nextEntries);
        });
      }

      setQuestionFocusSignal(null);
    },
    []
  );

  // Shared question validation for Next and Confirm.
  const runQuestionValidation = useCallback((): {
    isValid: boolean;
    surfaceErrors: () => void;
  } => {
    const result = validateQuestions(questionAnswersRef.current);

    return {
      isValid: result.isValid,
      surfaceErrors: () => {
        setQuestionErrors(result.errorsByFieldId);
        if (result.firstInvalidFieldId != null) {
          focusTickRef.current += 1;
          setQuestionFocusSignal({
            fieldId: result.firstInvalidFieldId,
            tick: focusTickRef.current
          });
        }
      }
    };
  }, []);

  // Empty deps by design: read latest answers from ref.
  const getQuestionSnapshots = useCallback((): QuestionSnapshot[] => {
    const currentAnswers = questionAnswersRef.current;
    if (currentAnswers.length === 0) return [];

    return currentAnswers
      .filter((answer) => answer.field != null)
      .map((answer) => {
        const { field } = answer;
        const notEntered = nothingEnteredText;
        let answerText = notEntered;

        switch (field.field_type) {
          case 'text_field':
          case 'text_area_field':
          case 'date_field':
            answerText = String(answer.data ?? '').trim() || notEntered;
            break;
          case 'radio_button_field':
          case 'select_field': {
            const choice = field.field_choices?.find((c) => c.id === answer.field_choice_id);
            answerText = choice?.name ?? notEntered;
            break;
          }
          case 'check_box_field': {
            const selectedNames = (answer.field_choice_ids ?? [])
              .map((id) => field.field_choices?.find((c) => c.id === id)?.name?.trim())
              .filter(Boolean) as string[];
            if (selectedNames.length === 0) {
              answerText = notEntered;
            } else if (selectedNames.length === 1) {
              [answerText] = selectedNames;
            } else if (selectedNames.length === 2) {
              answerText = `${selectedNames[0]} and ${selectedNames[1]}`;
            } else {
              answerText = `${selectedNames.slice(0, -1).join(', ')}, and ${selectedNames[selectedNames.length - 1]}`;
            }
            break;
          }
          case 'rank_field': {
            if (typeof answer.data === 'object' && answer.data !== null) {
              const entries = Object.entries(answer.data)
                .map(([choiceId, rank]) => {
                  const choice = field.field_choices?.find((c) => String(c.id) === choiceId);
                  return { name: choice?.name ?? choiceId, rank: Number(rank) };
                })
                .sort((a, b) => a.rank - b.rank);
              answerText =
                entries.length > 0
                  ? entries.map((e) => `${e.rank} - ${e.name}`).join('\n')
                  : notEntered;
            }
            break;
          }
          default:
            answerText = String(answer.data ?? '').trim() || notEntered;
        }

        return {
          fieldId: field.id,
          title: field.title,
          answer: answerText,
          required: field.required
        };
      });
  }, [nothingEnteredText]);

  const buildSnapshot = useCallback(
    (form: HTMLFormElement): PreviewSnapshot => {
      const formData = new FormData(form);
      const snapshotAttachments: PreviewSnapshot['attachments'] = [];

      attachments.forEach((att) => {
        const slot = slotStatesRef.current[att.key];
        let fileName = nothingSubmittedText;

        if (slot?.status === 'existing' || slot?.status === 'selected') {
          fileName = slot.displayName;
        }

        snapshotAttachments.push({
          name: att.name,
          fileName,
          mandatory: att.mandatory
        });
      });

      const sourceId = String(formData.get('application[source_id]') ?? '');
      const matchedSource = sourceId ? sources.find((s) => String(s.id) === sourceId) : undefined;

      // Build candidate preview rows from known form fields.
      // For selects, use option text instead of raw ID.
      const notEntered = nothingEnteredText;
      const candidateFields: PreviewSnapshot['candidateFields'] = [];
      CANDIDATE_FIELD_MAP.forEach(({ key, fullWidth }) => {
        const el = form.elements.namedItem(key);
        if (!el) return;
        if (el instanceof HTMLSelectElement) {
          const text = el.selectedIndex > 0 ? (el.selectedOptions[0]?.text ?? '') : '';
          const previewLabel = el.dataset.previewLabel?.trim();
          candidateFields.push({
            label: previewLabel && previewLabel.length > 0 ? previewLabel : key,
            value: text || notEntered,
            required: el.required,
            fullWidth
          });
        } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
          const val = el.value.trim();
          const previewLabel = el.dataset.previewLabel?.trim();
          candidateFields.push({
            label: previewLabel && previewLabel.length > 0 ? previewLabel : key,
            value: val || notEntered,
            required: el.required,
            fullWidth
          });
        }
      });

      return {
        candidateFields,
        questions: getQuestionSnapshots(),
        attachments: snapshotAttachments,
        hasAttachmentsSection: attachments.length > 0,
        hasQuestionsSection: parsedQuestionAnswers.length > 0,
        hasSourceSection: sources.length > 0,
        source: sourceId
          ? {
              id: sourceId,
              name: matchedSource?.name ?? sourceId
            }
          : null,
        privacy: privacy
          ? {
              headingText: privacy.headingText,
              descriptionHtml: privacy.descriptionHtml,
              agreeLabel: privacy.agreeLabel
            }
          : null
      };
    },
    [
      attachments,
      getQuestionSnapshots,
      nothingEnteredText,
      nothingSubmittedText,
      parsedQuestionAnswers.length,
      privacy,
      sources
    ]
  );

  const handleEdit = () => {
    setMode('edit');
  };

  const handleSourceChange = () => {
    if (sourceValidationError) {
      setSourceValidationError('');
    }
  };

  const handleAttachmentChange = useCallback((attachmentKey: string) => {
    setAttachmentErrors((prev) => {
      const nextEntries = Object.entries(prev).filter(([key]) => key !== attachmentKey);
      if (nextEntries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(nextEntries);
    });
  }, []);

  const handleSlotStatesChange = useCallback((states: Record<string, AttachmentSlotState>) => {
    slotStatesRef.current = states;
  }, []);

  const handleParsingStateChange = useCallback((parsing: boolean, error = '') => {
    setIsParsing(parsing);
    setParseError(error);
  }, []);

  const resumeSlotOverride = useMemo(() => {
    if (!selectedResumeFile || !resumeAttachment) {
      return null;
    }

    return {
      key: resumeAttachment.key,
      slot: {
        status: 'selected' as const,
        displayName: selectedResumeFile.name,
        pendingFile: selectedResumeFile,
        // A slot's originalExisting never changes after it is built from the attachment config
        // (Attachments carries it through every update), so derive it here instead of reading the
        // live slot-state ref during render.
        originalExisting:
          buildInitialAttachmentSlotStates([resumeAttachment])[resumeAttachment.key]
            ?.originalExisting ?? null,
        removedExisting: false
      }
    };
  }, [resumeAttachment, selectedResumeFile]);

  const isEdit = mode === 'edit';
  const isPreview = mode === 'preview';
  const handleSaveDialogClose = useCallback(() => {
    saveConfirmedRef.current = false;
    setSaveDialogOpen(false);
  }, []);

  const handleSaveConfirm = useCallback(() => {
    const form = document.getElementById(WCAG_FORM_ID) as HTMLFormElement | null;
    const saveButton = document.getElementById(
      'application_save_for_later'
    ) as HTMLButtonElement | null;

    setSaveDialogOpen(false);

    if (!form || !saveButton) {
      saveConfirmedRef.current = false;
      return;
    }

    saveConfirmedRef.current = true;
    form.requestSubmit(saveButton);
  }, []);

  const handleStartOverDialogClose = useCallback(() => {
    setStartOverDialogOpen(false);
  }, []);

  const handleStartOverConfirm = useCallback(() => {
    const form = document.getElementById(WCAG_FORM_ID) as HTMLFormElement | null;
    const startOverSubmitter = startOverSubmitRef.current;

    setStartOverDialogOpen(false);

    if (!form || !startOverSubmitter) {
      return;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(startOverSubmitter);
      return;
    }

    startOverSubmitter.click();
  }, []);

  // Register form submit + candidate input listeners.
  useEffect(() => {
    const form = document.getElementById(WCAG_FORM_ID) as HTMLFormElement | null;
    if (!form) return undefined;

    const candidateSection = document.getElementById('candidate-details');
    if (candidateSection) {
      candidateSection.addEventListener('input', handleCandidateInput);
      candidateSection.addEventListener('change', handleCandidateInput);
    }

    const stages = buildValidationStages({
      form,
      attachments,
      attachmentSlotStatesRef: slotStatesRef,
      sourcesLength: sources.length,
      privacy,
      runQuestionValidation,
      setAttachmentErrors,
      setSourceValidationError,
      setPrivacyError
    });

    const resetValidationState = () => {
      clearCandidateErrors();
      setSourceValidationError('');
      setAttachmentErrors({});
      setQuestionErrors({});
      setQuestionFocusSignal(null);
      setPrivacyError('');
    };

    // Next can surface immediately in edit mode.
    // Confirm must switch preview -> edit before surfacing.
    const runNextFlow = () => {
      const pipeline = runPipeline(stages);

      resetValidationState();

      if (!pipeline.allValid) {
        pipeline.surfaceFirstBlocker();
        setSkipToErrorsVisible(true);
        return;
      }

      setSkipToErrorsVisible(false);
      setSnapshot(buildSnapshot(form));
      setMode('preview');
      window.scrollTo({ top: 0, behavior: preferredScrollBehavior() });
    };

    const runSaveFlow = () => {
      const candidateStage = buildSaveCandidateStage();

      resetValidationState();

      if (!candidateStage.valid) {
        candidateStage.surface();
        setSkipToErrorsVisible(true);
        return;
      }

      setSkipToErrorsVisible(false);
      setSaveDialogOpen(true);
    };

    const handleSubmit = (e: SubmitEvent) => {
      const submitter = e.submitter as HTMLButtonElement | HTMLInputElement | null;
      const commitValue = resolveCommitValue(submitter);

      // Block submits with no commit value (for example bare form.submit()).
      if (!commitValue) {
        e.preventDefault();
        return;
      }

      if (submittingRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (isParsing) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (commitValue === 'Save') {
        if (saveConfirmedRef.current) {
          saveConfirmedRef.current = false;
          return;
        }

        e.preventDefault();
        runSaveFlow();
        return;
      }

      if (commitValue === 'Confirm') {
        const pipeline = runPipeline(stages);

        if (!pipeline.allValid) {
          e.preventDefault();
          setMode('edit');
          resetValidationState();
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              pipeline.surfaceFirstBlocker();
              setSkipToErrorsVisible(true);
            });
          });
        }
        // If valid, let native submit continue to Rails.
        submittingRef.current = true;
        setIsSubmitting(true);
        if (submitter) {
          lockSubmitterForCommit(form, submitter, commitValue);
        }
        return;
      }

      if (commitValue === 'Next') {
        e.preventDefault();
        e.stopPropagation();
        runNextFlow();
      }
    };

    const handleImplicitEnterSubmit = (e: KeyboardEvent) => {
      if (!isEdit || isParsing || e.defaultPrevented || e.key !== 'Enter') return;
      if (e.isComposing || e.keyCode === 229) return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

      const { target } = e;
      if (!(target instanceof HTMLElement)) return;
      if (!form.contains(target)) return;
      if (target.isContentEditable || target instanceof HTMLTextAreaElement) return;
      if (target.getAttribute('role') === 'combobox') return;
      if (target instanceof HTMLButtonElement || target instanceof HTMLSelectElement) return;

      if (target instanceof HTMLInputElement) {
        const type = target.type.toLowerCase();
        if (
          type === 'submit' ||
          type === 'button' ||
          type === 'reset' ||
          type === 'file' ||
          type === 'checkbox' ||
          type === 'radio'
        ) {
          return;
        }
      }

      const nextButton = document.getElementById(NEXT_BUTTON_ID);
      if (!(nextButton instanceof HTMLButtonElement)) return;

      e.preventDefault();
      e.stopPropagation();
      form.requestSubmit(nextButton);
    };

    form.addEventListener('submit', handleSubmit);
    form.addEventListener('keydown', handleImplicitEnterSubmit);

    return () => {
      form.removeEventListener('submit', handleSubmit);
      form.removeEventListener('keydown', handleImplicitEnterSubmit);
      if (candidateSection) {
        candidateSection.removeEventListener('input', handleCandidateInput);
        candidateSection.removeEventListener('change', handleCandidateInput);
      }
      clearCandidateErrors();
      setSkipToErrorsVisible(false);
    };
  }, [
    attachments,
    buildSnapshot,
    isEdit,
    isParsing,
    privacy,
    runQuestionValidation,
    sources.length
  ]);

  useEffect(() => {
    const skipToFormLink = document.getElementById(SKIP_TO_FORM_LINK_ID);
    const skipToErrorsLink = document.getElementById(SKIP_TO_ERRORS_LINK_ID);
    if (
      !(skipToFormLink instanceof HTMLAnchorElement) &&
      !(skipToErrorsLink instanceof HTMLAnchorElement)
    ) {
      return undefined;
    }

    const handleSkipToForm = (e: MouseEvent) => {
      e.preventDefault();

      const form = document.getElementById(WCAG_FORM_ID);
      if (!(form instanceof HTMLElement)) return;

      form.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
      focusWithoutScroll(form);
    };

    const handleSkipToErrors = (e: MouseEvent) => {
      e.preventDefault();

      if (!isEdit) {
        const form = document.getElementById(WCAG_FORM_ID);
        if (form instanceof HTMLElement) {
          form.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
          focusWithoutScroll(form);
        }
        return;
      }

      const target = findFirstValidationErrorTarget();
      if (target) {
        target.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'center' });
        focusWithTabindexCleanup(target);
        return;
      }

      const fallbackHeading = document.getElementById('candidate-details-heading');
      if (fallbackHeading instanceof HTMLElement) {
        fallbackHeading.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'center' });
        focusWithTabindexCleanup(fallbackHeading);
      }
    };

    skipToFormLink?.addEventListener('click', handleSkipToForm);
    skipToErrorsLink?.addEventListener('click', handleSkipToErrors);
    return () => {
      skipToFormLink?.removeEventListener('click', handleSkipToForm);
      skipToErrorsLink?.removeEventListener('click', handleSkipToErrors);
    };
  }, [isEdit]);

  useEffect(() => {
    const jobRoot = document.getElementById('job');
    document.body.classList.toggle('wcag-preview-mode', isPreview);
    if (jobRoot) {
      jobRoot.classList.toggle('wcag-preview-mode', isPreview);
    }
    return () => {
      document.body.classList.remove('wcag-preview-mode');
      if (jobRoot) {
        jobRoot.classList.remove('wcag-preview-mode');
      }
    };
  }, [isPreview]);

  return (
    <>
      <div
        aria-hidden={!isEdit}
        data-react-class="WCAG/EditContainer"
        style={{ display: isEdit ? 'block' : 'none' }}
      >
        {automatedDecisionNotice ? (
          <AutomatedDecisionNotice
            body={automatedDecisionNotice.body}
            title={automatedDecisionNotice.title}
          />
        ) : null}
        {privacy ? (
          <div data-react-class="WCAG/PrivacyAcknowledgement">
            <PrivacyAcknowledgement
              agreeLabel={privacy.agreeLabel}
              descriptionHtml={privacy.descriptionHtml}
              error={privacyError}
              headingText={privacy.headingText}
              initialChecked={privacy.initialChecked}
              onChange={(checked) => {
                if (checked) setPrivacyError('');
              }}
            />
          </div>
        ) : null}
        <div data-react-class="WCAG/CandidateDetails">
          <CandidateDetails
            autoFillConfig={autoFillConfig}
            candidateDetailsText={candidateDetailsText}
            formHtml={candidateFormHtml}
            isParsing={isParsing}
            onParsingStateChange={handleParsingStateChange}
            onSelectedResumeFileChange={setSelectedResumeFile}
            parseError={parseError}
            personalInformationLabel={personalInformationLabel}
            selectedResumeFile={selectedResumeFile}
          />
        </div>
        {parsedQuestionAnswers.length > 0 ? (
          <div data-react-class="WCAG/Questions">
            <Questions
              aiInterviewEnabled={aiInterviewEnabled}
              answers={parsedQuestionAnswers}
              errorsByFieldId={questionErrors}
              focusSignal={questionFocusSignal}
              onAnswersChange={handleAnswersChange}
            />
          </div>
        ) : null}
        {aiInterviewEnabled ? <AIInterviewModal jobId={jobId} /> : null}
        {attachments.length > 0 ? (
          <div data-react-class="WCAG/Attachments">
            <Attachments
              attachments={attachments}
              attachmentText={attachmentText}
              errorsByKey={attachmentErrors}
              externalSlotOverride={resumeSlotOverride}
              isParsing={isParsing}
              onAttachmentChange={handleAttachmentChange}
              onSlotStatesChange={handleSlotStatesChange}
            />
          </div>
        ) : null}
        {sources.length > 0 ? (
          <div data-react-class="WCAG/SourceSelection">
            <SourceSelection
              onSourceChange={handleSourceChange}
              sourceError={sourceValidationError}
              sources={sources}
              sourceValue={sourceValue}
            />
          </div>
        ) : null}
        <div data-react-class="WCAG/FormActions">
          <FormActions
            isDisabled={isParsing}
            nextText={nextText}
            saveForLaterText={saveForLaterText}
            showSaveForLater={showSaveForLater}
          />
        </div>
      </div>

      <div
        aria-hidden={!isPreview}
        data-react-class="WCAG/PreviewContainer"
        style={{ display: isPreview ? 'block' : 'none' }}
      >
        <fieldset disabled style={{ border: 'none', margin: 0, padding: 0 }}>
          <Preview
            nothingEnteredText={nothingEnteredText}
            nothingSubmittedText={nothingSubmittedText}
            personalInformationLabel={personalInformationLabel}
            previewAriaLabel={previewAriaLabel}
            previewInfoBannerText={previewInfoBannerText}
            snapshot={snapshot}
          />
        </fieldset>

        <Box id="wcag-preview-actions" sx={classes.preview.actionsSx()}>
          <Button
            aria-label="Edit application"
            id="wcag-preview-edit"
            onClick={handleEdit}
            sx={classes.preview.editButtonSx()}
            type="button"
          >
            {editButtonText}
          </Button>
          <Button
            aria-label={startOverButtonText}
            id="wcag-preview-startover"
            onClick={() => setStartOverDialogOpen(true)}
            sx={classes.preview.startOverButtonSx()}
            type="button"
          >
            {startOverButtonText}
          </Button>
          <Button
            formNoValidate
            aria-label="Confirm and submit application"
            disabled={isSubmitting}
            form={WCAG_FORM_ID}
            id="wcag-preview-submit"
            name="commit"
            sx={classes.preview.confirmButtonSx()}
            type="submit"
            value="Confirm"
          >
            {confirmButtonText}
          </Button>
        </Box>
      </div>

      <input
        ref={startOverSubmitRef}
        aria-hidden="true"
        form={WCAG_FORM_ID}
        name="commit"
        style={{ display: 'none' }}
        tabIndex={-1}
        type="submit"
        // Keep the submit value aligned with JobsController#apply's existing redirect branch.
        value={START_OVER_COMMIT_VALUE}
      />

      <Dialog
        aria-describedby="wcag-save-dialog-description"
        aria-labelledby="wcag-save-dialog-title"
        onClose={handleSaveDialogClose}
        open={saveDialogOpen}
        slotProps={{ paper: { sx: classes.saveDialog.paperSx() } }}
      >
        <DialogTitle id="wcag-save-dialog-title" sx={classes.saveDialog.titleSx()}>
          {saveDialogTitle}
        </DialogTitle>
        <DialogContent sx={classes.saveDialog.contentSx()}>
          <Typography id="wcag-save-dialog-description" sx={classes.saveDialog.messageSx()}>
            {confirmSaveMessage}
          </Typography>
          <Typography sx={classes.saveDialog.noteSx()}>{confirmSaveNote}</Typography>
        </DialogContent>
        <DialogActions sx={classes.saveDialog.actionsSx()}>
          <Button
            id="wcag-save-dialog-cancel"
            onClick={handleSaveDialogClose}
            sx={classes.saveDialog.cancelButtonSx()}
            type="button"
          >
            {cancelText}
          </Button>
          <Button
            autoFocus
            id="wcag-save-dialog-confirm"
            onClick={handleSaveConfirm}
            sx={classes.saveDialog.confirmButtonSx()}
            type="button"
          >
            {saveDialogConfirmText}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-describedby="wcag-startover-dialog-description"
        aria-labelledby="wcag-startover-dialog-title"
        onClose={handleStartOverDialogClose}
        open={startOverDialogOpen}
        slotProps={{ paper: { sx: classes.startOverDialog.paperSx() } }}
      >
        <DialogTitle id="wcag-startover-dialog-title" sx={classes.startOverDialog.titleSx()}>
          {startOverDialogTitle}
        </DialogTitle>
        <DialogContent sx={classes.startOverDialog.contentSx()}>
          <Typography
            id="wcag-startover-dialog-description"
            sx={classes.startOverDialog.messageSx()}
          >
            {startOverDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={classes.startOverDialog.actionsSx()}>
          <Button
            autoFocus
            id="wcag-startover-dialog-cancel"
            onClick={handleStartOverDialogClose}
            sx={classes.startOverDialog.cancelButtonSx()}
            type="button"
          >
            {cancelText}
          </Button>
          <Button
            id="wcag-startover-dialog-confirm"
            onClick={handleStartOverConfirm}
            sx={classes.startOverDialog.confirmButtonSx()}
            type="button"
          >
            {startOverButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationFlow;
